import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import PetCard from '@/components/PetCard';
import { Button } from '@/components/ui/button';
import { 
  Search, 
  MapPin, 
  ArrowRight,
  CheckCircle
} from 'lucide-react';

// URLs das ilustrações originais
const LOGO_URL = "https://customer-assets.emergentagent.com/job_pet-photo-storage/artifacts/343ueuet_logo.png";
const LOCALIZACAO_URL = "https://customer-assets.emergentagent.com/job_pet-photo-storage/artifacts/ranuh2r5_localizacao.png";
const AURORA_URL = "https://customer-assets.emergentagent.com/job_pet-photo-storage/artifacts/5t54oucu_ong.png";
const FUNDO_URL = "https://customer-assets.emergentagent.com/job_pet-photo-storage/artifacts/ga1btmlj_fundo.jpeg";

const HomePage = () => {
  const [stats, setStats] = useState({ total_pets: 0, perdidos: 0, encontrados: 0, adocao: 0 });
  const [recentPets, setRecentPets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, petsRes] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/pets`)
        ]);
        setStats(statsRes.data);
        setRecentPets(petsRes.data.slice(0, 6));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#F5D79C]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-[#FCEBC5] px-4 py-2 rounded-full border border-[#C4956A]">
                <span>🐾</span>
                <span className="text-sm font-medium text-[#5D4A3A]">Ajude um pet hoje!</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-[#5D4A3A] leading-tight">
                Olá, eu sou a Aurora!
              </h1>
              
              <p className="text-lg text-[#5D4A3A]">
                Seja bem-vindo(a) ao Miaucaomigo! Minha humana, Anna Paula, criou este aplicativo para que, juntos, 
                possamos ajudar animais abandonados a serem vistos, protegidos e encontrarem um lar cheio de amor.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/cadastrar-pet">
                  <Button className="btn-primary px-8 py-6 text-lg" data-testid="cta-cadastrar-pet">
                    <span className="mr-2">🐾</span>
                    Cadastrar Pet
                  </Button>
                </Link>
                <Link to="/galeria">
                  <Button variant="outline" className="px-8 py-6 text-lg border-2 border-[#6B5344] text-[#5D4A3A] bg-[#FCEBC5] hover:bg-[#F5E6C8] rounded-full" data-testid="cta-ver-galeria">
                    <Search className="w-5 h-5 mr-2" />
                    Ver Galeria
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Illustration */}
            <div className="relative animate-float">
              <div className="relative w-full max-w-md mx-auto">
                <div className="logo-container w-96 h-96 mx-auto flex items-center justify-center p-4">
                  <img 
                    src={AURORA_URL} 
                    alt="Aurora - Gato" 
                    className="w-full h-full object-contain"
                  />
                </div>
                
                {/* Floating Cards */}
                <div className="absolute -left-4 top-1/4 bg-[#FCEBC5] rounded-2xl p-4 shadow-lg border border-[#C4956A]">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#81C784] rounded-full flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-[#5D4A3A]">{stats.encontrados}+</p>
                      <p className="text-xs text-[#7A5B3E]">Encontrados</p>
                    </div>
                  </div>
                </div>
                
                <div className="absolute -right-4 bottom-1/4 bg-[#FCEBC5] rounded-2xl p-4 shadow-lg border border-[#C4956A]">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#64B5F6] rounded-full flex items-center justify-center">
                      <span className="text-xl">❤️</span>
                    </div>
                    <div>
                      <p className="font-bold text-[#5D4A3A]">{stats.adocao}+</p>
                      <p className="text-xs text-[#7A5B3E]">Para Adoção</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-[#FCEBC5]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-6 rounded-2xl bg-[#F5D79C] border border-[#C4956A]">
              <p className="text-3xl font-bold text-[#5D4A3A]">{stats.total_pets}</p>
              <p className="text-sm text-[#7A5B3E] mt-1">Pets Cadastrados</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-[#FFCDD2]">
              <p className="text-3xl font-bold text-[#C62828]">{stats.perdidos}</p>
              <p className="text-sm text-[#5D4A3A] mt-1">Perdidos</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-[#C8E6C9]">
              <p className="text-3xl font-bold text-[#2E7D32]">{stats.encontrados}</p>
              <p className="text-sm text-[#5D4A3A] mt-1">Encontrados</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-[#BBDEFB]">
              <p className="text-3xl font-bold text-[#1565C0]">{stats.adocao}</p>
              <p className="text-sm text-[#5D4A3A] mt-1">Para Adoção</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Com Sheik */}
      <section className="py-16 bg-[#F5D79C]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="logo-container w-40 h-40 mx-auto mb-4 flex items-center justify-center p-3">
              <img 
                src={LOCALIZACAO_URL} 
                alt="Sheik - Cachorro com lupa" 
                className="w-full h-full object-contain"
              />
            </div>
            <h2 className="text-3xl font-display font-bold text-[#5D4A3A] mb-4">Olá, eu sou o Sheik!</h2>
            <p className="text-[#5D4A3A] max-w-2xl mx-auto">
              Viu um pet na rua? Tire uma foto na hora! O Miaucaomigo registra o local exato via GPS.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '👤', title: 'Crie sua Conta', desc: 'Cadastre-se gratuitamente em poucos segundos' },
              { icon: '🐾', title: 'Cadastre o Pet', desc: 'Adicione fotos, localização e descrição do animal' },
              { icon: '❤️', title: 'Conecte-se', desc: 'Encontre ou ajude outros a encontrar seus pets' },
            ].map((step, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#FCEBC5] border-2 border-[#C4956A] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="text-3xl">{step.icon}</span>
                </div>
                <div className="w-8 h-8 mx-auto mb-4 rounded-full bg-[#6B5344] text-white font-bold flex items-center justify-center">
                  {index + 1}
                </div>
                <h3 className="text-xl font-bold text-[#5D4A3A] mb-2">{step.title}</h3>
                <p className="text-[#7A5B3E]">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7 Days Info */}
      <section className="py-16 bg-[#FCEBC5]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="logo-container w-40 h-40 mx-auto mb-4 flex items-center justify-center p-3">
            <img 
              src={LOGO_URL} 
              alt="Logo Miaucaomigo" 
              className="w-full h-full object-contain"
            />
          </div>
          <h2 className="text-3xl font-display font-bold text-[#5D4A3A] mb-4">Registros de 7 Dias</h2>
          <p className="text-[#5D4A3A] max-w-2xl mx-auto">
            Para manter a rede sempre atualizada com os últimos passos desse animalzinho, os registros expiram após uma semana. 
            Assim, aguardamos que uma nova pessoa possa avistá-lo e marcar novamente sua localização.
          </p>
        </div>
      </section>

      {/* Recent Pets */}
      <section className="py-16 bg-[#F5D79C]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-display font-bold text-[#5D4A3A]">Pets Recentes</h2>
              <p className="text-[#7A5B3E] mt-2">Veja os últimos pets cadastrados na plataforma</p>
            </div>
            <Link to="/galeria">
              <Button variant="outline" className="rounded-full border-[#6B5344] text-[#5D4A3A] bg-[#FCEBC5] hover:bg-[#F5E6C8]">
                Ver Todos
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-[#FCEBC5] rounded-2xl h-80 animate-pulse border border-[#C4956A]"></div>
              ))}
            </div>
          ) : recentPets.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentPets.map(pet => (
                <PetCard key={pet.id} pet={pet} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-[#FCEBC5] rounded-2xl border border-[#C4956A]">
              <span className="text-6xl">🐾</span>
              <p className="text-[#7A5B3E] mt-4">Nenhum pet cadastrado ainda. Seja o primeiro!</p>
              <Link to="/cadastrar-pet">
                <Button className="mt-4 btn-primary">
                  Cadastrar Pet
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#6B5344]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-[#FCEBC5] mb-4">
            Perdeu seu pet? Encontrou um animal?
          </h2>
          <p className="text-[#F5E6C8] text-lg mb-8 max-w-2xl mx-auto">
            Cadastre agora e aumente as chances de reencontro. Nossa comunidade está pronta para ajudar!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/cadastrar-pet">
              <Button className="bg-[#F5D79C] text-[#5D4A3A] hover:bg-[#FCEBC5] px-8 py-6 text-lg rounded-full">
                <span className="mr-2">🐾</span>
                Cadastrar Agora
              </Button>
            </Link>
            <Link to="/mapa">
              <Button variant="outline" className="border-2 border-[#F5D79C] text-[#FCEBC5] hover:bg-[#7A5B3E] px-8 py-6 text-lg rounded-full">
                <MapPin className="w-5 h-5 mr-2" />
                Ver no Mapa
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
